import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  Dropdown,
  EmptyState,
  Input,
  PageHeader,
  Select,
  SkeletonRows,
  Table,
  statusTone,
  useToast,
} from "../components/ui";
import type { Column } from "../components/ui";
import { projectService, type Project } from "../services/project.service";
import { formatRelative } from "../lib/format";
import { plainStatus, useAdvancedMode } from "../lib/plain-language";

type PendingAction = { type: "archive" | "delete"; project: Project } | null;

export default function ProjectsPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("");
  const [advanced] = useAdvancedMode();
  const [pending, setPending] = useState<PendingAction>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setProjects(await projectService.list());
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Falha ao carregar projetos");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const filtered = useMemo(
    () =>
      projects.filter(
        (project) =>
          (!status || project.status === status) &&
          project.name.toLowerCase().includes(query.trim().toLowerCase()),
      ),
    [projects, query, status],
  );

  async function duplicate(project: Project) {
    try {
      await projectService.duplicate(project.id);
      toast.success(`"${project.name}" duplicado.`);
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Falha ao duplicar");
    }
  }

  async function runPending() {
    if (!pending) return;
    const { type, project } = pending;
    try {
      if (type === "archive") {
        await projectService.archive(project.id);
        toast.success(project.status === "archived" ? "Sistema reativado." : "Sistema arquivado.");
      } else {
        await projectService.remove(project.id);
        toast.success("Projeto excluído.");
      }
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Falha na operação");
    } finally {
      setPending(null);
    }
  }

  const columns: Column<Project>[] = [
    {
      key: "name",
      header: "Nome",
      render: (project) => (
        <div className="min-w-0">
          <p className="truncate font-medium text-ink">{project.name}</p>
          <p className="truncate font-mono text-xs text-ink-muted">{project.slug}</p>
        </div>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (project) => <Badge tone={statusTone(project.status)}>{plainStatus(project.status, advanced)}</Badge>,
    },
    {
      key: "updated",
      header: "Última alteração",
      hideOnMobile: true,
      render: (project) => (
        <span className="font-mono text-xs text-ink-muted">
          {formatRelative(project.last_activity_at)}
        </span>
      ),
    },
    {
      key: "template",
      header: "Ponto de partida",
      hideOnMobile: true,
      render: (project) => (
        <span className="text-xs text-ink-muted">{project.template?.name ?? "—"}</span>
      ),
    },
    {
      key: "preview",
      header: "Último preview",
      hideOnMobile: true,
      render: (project) =>
        project.last_preview ? (
          <Badge tone={statusTone(project.last_preview.status)}>
            {plainStatus(project.last_preview.status, advanced)}
          </Badge>
        ) : (
          <span className="text-xs text-ink-muted">—</span>
        ),
    },
    {
      key: "actions",
      header: "",
      align: "right",
      render: (project) => (
        <Dropdown
          trigger={
            <Button variant="ghost" size="sm" aria-label="Ações">
              ⋯
            </Button>
          }
          items={[
            { label: "Abrir", onSelect: () => navigate(`/projects/${project.id}`) },
            { label: "Duplicar", onSelect: () => void duplicate(project) },
            {
              label: project.status === "archived" ? "Reativar" : "Arquivar",
              onSelect: () => setPending({ type: "archive", project }),
            },
            {
              label: "Excluir",
              destructive: true,
              onSelect: () => setPending({ type: "delete", project }),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Meus sistemas"
        description="Todos os projetos dentro do seu escopo."
        actions={
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => navigate("/projects/new/wizard")}>Criar sistema com IA</Button>
            <Button variant="ghost" onClick={() => navigate("/projects/new")}>
              Novo projeto
            </Button>
          </div>
        }
      />

      <Card>
        <div className="mb-4 grid gap-3 sm:grid-cols-[1fr_12rem]">
          <Input
            placeholder="Buscar por nome…"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
          <Select
            value={status}
            onChange={(event) => setStatus(event.target.value)}
            placeholder="Todas as situações"
            options={[
              { value: "draft", label: "Rascunho" },
              { value: "active", label: "Ativo" },
              { value: "building", label: "Construindo" },
              { value: "archived", label: "Arquivado" },
            ]}
          />
        </div>

        {error && (
          <p className="mb-4 rounded-md border border-danger/40 bg-danger/10 p-3 text-sm text-danger">
            {error}
          </p>
        )}

        {loading ? (
          <SkeletonRows rows={4} />
        ) : (
          <Table
            columns={columns}
            rows={filtered}
            rowKey={(project) => project.id}
            empty={
              <EmptyState
                title={projects.length ? "Nenhum projeto com esse filtro" : "Nenhum projeto ainda"}
                description={
                  projects.length
                    ? "Tente outra busca ou outra situação."
                    : "Crie o primeiro projeto do seu time."
                }
                action={
                  !projects.length ? (
                    <Button size="sm" onClick={() => navigate("/projects/new")}>
                      Novo projeto
                    </Button>
                  ) : undefined
                }
              />
            }
          />
        )}
      </Card>

      <ConfirmDialog
        open={pending !== null}
        title={
          pending?.type === "delete"
            ? "Excluir projeto"
            : pending?.project.status === "archived"
              ? "Reativar projeto"
              : "Arquivar projeto"
        }
        description={
          pending?.type === "delete"
            ? `"${pending.project.name}" será excluído. Esta ação pode ser revertida apenas pelo suporte.`
            : `Confirmar a mudança de situação de "${pending?.project.name}"?`
        }
        destructive={pending?.type === "delete"}
        confirmLabel={pending?.type === "delete" ? "Excluir" : "Confirmar"}
        onConfirm={runPending}
        onCancel={() => setPending(null)}
      />
    </>
  );
}
